import React, { useEffect, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { message, Button } from "antd";
import { EyeOutlined, SyncOutlined } from "@ant-design/icons";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import {
  AllCommunityModule,
  ModuleRegistry,
  provideGlobalGridOptions,
} from "ag-grid-community";
import { fetchNMITransactions, syncNMITransactions } from "../../core/api/data";
import formatDateWithTimezone from "../../utils/dateUtils";
import {dateFilterParams} from "../../utils/dateUtils";
import dayjs from "dayjs";

ModuleRegistry.registerModules([AllCommunityModule]);
provideGlobalGridOptions({ theme: "legacy" });

const NMITransactionsList = ({ timezone, filters, refreshTrigger }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncInProgress, setSyncInProgress] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [error, setError] = useState(null);
    
  // Use a ref to track if a request is already in progress
  const requestInProgress = React.useRef(false);
  
  useEffect(() => {
    // Only proceed if we have filters and no request is in progress
    if (filters && filters.brands && filters.brands.length > 0 && !requestInProgress.current) {
      requestInProgress.current = true;
      setLoading(true);
      
      loadTransactions(filters.brands[0], filters)
        .finally(() => {
          requestInProgress.current = false;
        });
    } else if (!filters || !filters.brands || filters.brands.length === 0) {
      setLoading(false);
    }
  }, [filters, refreshTrigger]);
  
  const loadTransactions = async (brandId, currentFilters = null) => {
    setLoading(true);
    setError(null);
    
    // Return the promise so we can chain .finally() in the useEffect
    return fetchNMITransactions(brandId, currentFilters)
      .then(response => {
        if (response.results) {
          setTransactions(response.results);
          
          if (response.last_sync_time) {
            const syncDate = new Date(response.last_sync_time);
            setLastSyncTime(syncDate.toLocaleString());
          }
        } else {
          setTransactions(response);
        }
        return response;
      })
      .catch(error => {
        console.error("Error fetching NMI transactions:", error);
        setError("Failed to fetch NMI transactions. Please try again.");
        throw error; // Re-throw to propagate to the caller
      })
      .finally(() => {
        setLoading(false);
      });
  };
  
  const handleSyncTransactions = () => {
    if (!filters || !filters.brands || filters.brands.length === 0) {
      message.warning("Please select a brand first.");
      return;
    }
    
    const brandId = filters.brands[0];
    setSyncInProgress(true);
    message.loading({ content: "Syncing NMI transactions...", key: "syncMessage", duration: 0 });
    
    syncNMITransactions(brandId)
      .then(response => {
        message.success({ content: "NMI transactions synced successfully!", key: "syncMessage" });
        // Refresh transactions after sync
        return fetchNMITransactions(brandId, filters);
      })
      .then(response => {
        const transactionsData = response.results || response;
        
        if (response.last_sync_time) {
          const syncDate = new Date(response.last_sync_time);
          setLastSyncTime(syncDate.toLocaleString());
        }
        
        setTransactions(transactionsData);
        setSyncInProgress(false);
      })
      .catch(error => {
        console.error("Error syncing NMI transactions:", error);
        const errorMsg = getErrorMessage(error);
        setError(errorMsg);
        message.error({ content: `Failed to sync NMI transactions: ${errorMsg}`, key: "syncMessage" });
        setSyncInProgress(false);
      });
  };

  const transactionColumns = [
    {
      headerName: "Gateway",
      field: "gateway_account_name",
      sortable: true,
      filter: true,
      width: 150,
      valueGetter: (params) => {
        return params.data?.gateway_account_name || '';
      }
    },
    {
      headerName: "Transaction ID",
      field: "transaction_id",
      sortable: true,
      filter: true,
      width: 150
    },
    {
      headerName: "Type",
      field: "transaction_type",
      sortable: true,
      filter: true,
      width: 100
    },
    {
      headerName: "Status",
      field: "condition",
      sortable: true,
      filter: true,
      width: 120
    },
    {
      headerName: "Amount",
      field: "action_amount",
      sortable: true,
      filter: true,
      width: 120,
      valueFormatter: (params) => {
        if (params.value) {
          return `$${parseFloat(params.value).toFixed(2)}`;
        }
        return '';
      }
    },
    {
      headerName: "Date",
      field: "action_date",
      sortable: true,
      filter: true,
      width: 180,
      valueFormatter: (p) => formatDateWithTimezone(p.value, timezone),

    },
    {
      headerName: "Order ID",
      field: "order_id",
      sortable: true,
      filter: true,
      width: 120
    },
    {
      headerName: "Customer",
      field: "customer",
      sortable: true,
      filter: true,
      width: 180,
      valueGetter: (params) => {
        const firstName = params.data?.first_name || '';
        const lastName = params.data?.last_name || '';
        return `${firstName} ${lastName}`.trim();
      }
    },
    {
      headerName: "Email",
      field: "email",
      sortable: true,
      filter: true,
      width: 200
    },
    {
      headerName: "Address",
      field: "address",
      sortable: true,
      filter: true,
      width: 250,
      valueGetter: (params) => {
        const address1 = params.data?.address_1 || '';
        const address2 = params.data?.address_2 ? `, ${params.data.address_2}` : '';
        return `${address1}${address2}`;
      }
    },
    {
      headerName: "City",
      field: "city",
      sortable: true,
      filter: true,
      width: 120
    },
    {
      headerName: "State",
      field: "state",
      sortable: true,
      filter: true,
      width: 80
    },
    {
      headerName: "Postal Code",
      field: "postal_code",
      sortable: true,
      filter: true,
      width: 120
    },
    {
      headerName: "Country",
      field: "country",
      sortable: true,
      filter: true,
      width: 80
    },
    {
      headerName: "Phone",
      field: "phone",
      sortable: true,
      filter: true,
      width: 120
    },
    {
      headerName: "Card Number",
      field: "cc_number",
      sortable: true,
      filter: true,
      width: 150
    },
    {
      headerName: "Card Type",
      field: "cc_type",
      sortable: true,
      filter: true,
      width: 100
    },
    {
      headerName: "Entry Mode",
      field: "entry_mode",
      sortable: true,
      filter: true,
      width: 120
    },
    {
      headerName: "Auth Code",
      field: "authorization_code",
      sortable: true,
      filter: true,
      width: 120
    },
    {
      headerName: "Response",
      field: "action_response_text",
      sortable: true,
      filter: true,
      width: 150
    },
    {
      headerName: "Batch ID",
      field: "action_batch_id",
      sortable: true,
      filter: true,
      width: 120
    },
    {
      headerName: "Success",
      field: "action_success",
      sortable: true,
      filter: true,
      width: 100,
      valueFormatter: (params) => {
        return params.value === true ? 'Yes' : 'No';
      }
    },
  ];

  // Helper function to extract meaningful error messages
  const getErrorMessage = (error) => {
    if (!error) return "Unknown error occurred";
    
    // Handle Axios errors
    if (error.response) {
      // Server responded with a status code outside of 2xx range
      if (error.response.data && error.response.data.detail) {
        return error.response.data.detail;
      }
      
      if (error.response.status === 404) {
        return "API endpoint not found. Please check if the backend server is properly configured.";
      }
      
      if (error.response.status === 401) {
        return "Authentication failed. Please log in again.";
      }
      
      if (error.response.status === 403) {
        return "You don't have permission to access this resource.";
      }
      
      return `Server error: ${error.response.status} ${error.response.statusText || ''}`;
    } else if (error.request) {
      // Request was made but no response received
      return "No response from server. Please check your network connection.";
    } else if (error.message) {
      // Something else happened in setting up the request
      return error.message;
    }
    
    return "Unknown error occurred";
  };

  return (
    <div className="card">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="card-title mb-0">NMI Transactions</h4>
          <div className="d-flex align-items-center">
            {lastSyncTime && (
              <div className="me-3">
                <small>Last synced: {new Date(lastSyncTime).toLocaleString()}</small>
              </div>
            )}
            <Button
              type="primary"
              icon={<SyncOutlined spin={syncInProgress} />}
              loading={syncInProgress}
              onClick={handleSyncTransactions}
              disabled={!filters?.brands?.length || syncInProgress}
            >
              Sync
            </Button>
          </div>
        </div>
        
        {loading ? (
          <div className="text-center p-5">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : error ? (
          <div className="alert alert-danger" role="alert">
            <h5>Error</h5>
            <p>{error}</p>
            <button 
              className="btn btn-outline-danger" 
              onClick={() => {
                setError(null);
                if (filters?.brands?.length > 0) loadTransactions(filters.brands[0], filters);
              }}
            >
              Try Again
            </button>
          </div>
        ) : (
          <div className="ag-theme-quartz" style={{ height: 600, width: '100%' }}>
            <AgGridReact
              rowData={transactions}
              columnDefs={transactionColumns}
              pagination={false}
              defaultColDef={{
                flex: 1,
                minWidth: 100,
                resizable: true,
              }}
              sortModel={[{
                colId: 'action_date',
                sort: 'desc'
              }]}
              overlayNoRowsTemplate={
                transactions.length === 0 ? 
                "<div class='p-3'>No transactions found. Try syncing transactions using the Sync button above.</div>" : 
                "<div class='p-3'>No matching records found</div>"
              }
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default NMITransactionsList;
